import * as React from "react";
import {useContext, useEffect, useMemo, useState} from "react";

import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import {ProgressSpinner} from "primereact/progressspinner";

import styles from "./RestauranteAnalytics.module.css";

import CustomView from "../../component/CustomView";
import Calendario from "../../component/form/calendario/Calendario";
import Combo from "../../component/form/combo/Combo";
import Restaurante from "../../model/Restaurante";
import {
    IconeTipoAcesso,
    RestauranteAnalyticsTipoAcesso,
    RotuloTipoAcesso
} from "../../model/RestauranteAnalyticsTipoAcesso";
import {RestauranteAnalyticsResumo} from "../../dto/RestauranteAnalyticsResumo";
import {MensagemContext} from "../../context/MensagemContext";
import {MensagemContextType} from "../../context/MensagemContextType";
import {Ajax} from "../../utils/Ajax";
import rest from "../../utils/EnvConfig";
import {useKeycloak} from "../../keycloak/useKeycloak";
import {TipoUsuarioKeycloak} from "../../keycloak/TipoUsuarioKeycloak";

const LIMITE_CIDADES = 10;
const LIMITE_RESTAURANTES = 10;
const TODOS_RESTAURANTES = "__todos__";

const CORES_TIPO_ACESSO: Record<RestauranteAnalyticsTipoAcesso, string> = {
    [RestauranteAnalyticsTipoAcesso.VISUALIZOU_PERFIL]: "--blue-500",
    [RestauranteAnalyticsTipoAcesso.CLICOU_WHATSAPP]: "--green-500",
    [RestauranteAnalyticsTipoAcesso.CLICOU_CARDAPIO]: "--orange-500",
    [RestauranteAnalyticsTipoAcesso.CLICOU_ROTA]: "--purple-500",
};

const primeiroDiaDoMes = (): Date => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
}

function RestauranteAnalyticsView() {
    const {hasRole, user, token} = useKeycloak();
    const {error, warning} = useContext(MensagemContext) as MensagemContextType;
    const [restaurantes, setRestaurantes] = useState<Restaurante[]>([]);
    const [restauranteSelecionado, setRestauranteSelecionado] = useState<string>();
    const [dataInicial, setDataInicial] = useState<Date>(primeiroDiaDoMes());
    const [dataFinal, setDataFinal] = useState<Date>(new Date());
    const [resumo, setResumo] = useState<RestauranteAnalyticsResumo>();
    const [carregando, setCarregando] = useState<boolean>(false);

    useEffect(() => {
        Ajax.of()
            .error(error)
            .setTokenKeycloak(token!)
            .obterTodosCustom(obterRecursoRestaurantesCorreto(), (data) => {
                setRestaurantes(data);
            });
    }, []);

    useEffect(() => {
        pesquisar();
    }, [restauranteSelecionado]);

    const formatarDataParametro = (data: Date): string => {
        const mes = `${data.getMonth() + 1}`.padStart(2, "0");
        const dia = `${data.getDate()}`.padStart(2, "0");
        return `${data.getFullYear()}-${mes}-${dia}`;
    }

    const pesquisar = () => {
        if (!restauranteSelecionado) {
            setResumo(undefined);
            return;
        }

        if (dataInicial > dataFinal) {
            warning("A data inicial não pode ser maior que a data final.");
            return;
        }

        setCarregando(true);

        const periodo = `?dataInicio=${formatarDataParametro(dataInicial)}&dataFim=${formatarDataParametro(dataFinal)}`;

        Ajax.of()
            .error(error)
            .setTokenKeycloak(token!)
            .obterTodosCustom(`${obterRecursoResumo()}${periodo}`,
                (data: any) => {
                    setResumo(data as RestauranteAnalyticsResumo);
                    setCarregando(false);
                },
                () => setCarregando(false));
    }

    const opcoesRestaurantes = useMemo(() => {
        const rotuloTodos = hasRole(TipoUsuarioKeycloak.ADMIN)
            ? "Todos os restaurantes"
            : "Todos os meus restaurantes";

        return [{uuid: TODOS_RESTAURANTES, nomeEstabelecimento: rotuloTodos}, ...restaurantes];
    }, [restaurantes]);

    const obterRecursoResumo = (): string => {
        if (restauranteSelecionado === TODOS_RESTAURANTES) {
            return `${rest.restauranteAnalytics}/${rest.restauranteAnalyticsResumoGeral}`;
        }

        return `${rest.restauranteAnalytics}/${rest.restauranteAnalyticsResumo}${restauranteSelecionado}`;
    }

    const obterRecursoRestaurantesCorreto = (): string | undefined => {
        if (hasRole(TipoUsuarioKeycloak.ADMIN)) {
            return `${rest.restaurante}/${rest.obterTodos}`;
        }

        if (hasRole(TipoUsuarioKeycloak.CLIENTE)) {
            return `${rest.restaurante}/${rest.restaurantesPorUsuario}${user.id}`;
        }

        return "";
    }

    const obterCor = (tipoAcesso: RestauranteAnalyticsTipoAcesso): string => {
        return getComputedStyle(document.documentElement)
            .getPropertyValue(CORES_TIPO_ACESSO[tipoAcesso]).trim();
    }

    const formatarDia = (dia: string): string => {
        const [, mes, diaDoMes] = dia.split("-");
        return `${diaDoMes}/${mes}`;
    }

    const dadosTotaisPorTipo = useMemo(() => {
        const totais = resumo?.totaisPorTipo ?? [];

        return {
            labels: totais.map(total => RotuloTipoAcesso[total.tipoAcesso]),
            datasets: [
                {
                    data: totais.map(total => total.total),
                    backgroundColor: totais.map(total => obterCor(total.tipoAcesso)),
                }
            ]
        };
    }, [resumo]);

    const dadosTotaisPorRestaurante = useMemo(() => {
        const topRestaurantes = (resumo?.totaisPorRestaurante ?? []).slice(0, LIMITE_RESTAURANTES);

        return {
            labels: topRestaurantes.map(item => item.restaurante),
            datasets: [
                {
                    label: "Acessos",
                    data: topRestaurantes.map(item => item.total),
                    backgroundColor: obterCor(RestauranteAnalyticsTipoAcesso.CLICOU_ROTA),
                }
            ]
        };
    }, [resumo]);

    const dadosTotaisPorCidade = useMemo(() => {
        const cidades = (resumo?.totaisPorCidade ?? []).slice(0, LIMITE_CIDADES);

        return {
            labels: cidades.map(cidade => `${cidade.cidade}/${cidade.estado}`),
            datasets: [
                {
                    label: "Acessos",
                    data: cidades.map(cidade => cidade.total),
                    backgroundColor: obterCor(RestauranteAnalyticsTipoAcesso.VISUALIZOU_PERFIL),
                }
            ]
        };
    }, [resumo]);

    const dadosTotaisPorDia = useMemo(() => {
        const totais = resumo?.totaisPorDia ?? [];
        const dias = Array.from(new Set(totais.map(total => total.dia))).sort();

        return {
            labels: dias.map(formatarDia),
            datasets: Object.values(RestauranteAnalyticsTipoAcesso).map(tipoAcesso => ({
                label: RotuloTipoAcesso[tipoAcesso],
                borderColor: obterCor(tipoAcesso),
                backgroundColor: obterCor(tipoAcesso),
                tension: 0.3,
                data: dias.map(dia =>
                    totais.find(total => total.dia === dia && total.tipoAcesso === tipoAcesso)?.total ?? 0)
            }))
        };
    }, [resumo]);

    const opcoesRosca = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom"
            }
        }
    };

    const opcoesBarra = {
        indexAxis: "y",
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    const opcoesLinha = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom"
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    const conteudo = () => {
        if (carregando) {
            return (
                <center>
                    <ProgressSpinner/>
                </center>
            );
        }

        if (!restauranteSelecionado) {
            return (
                <div className={styles.semDados}>
                    Selecione um restaurante para visualizar os acessos do período.
                </div>
            );
        }

        if (!resumo || resumo.totalGeral === 0) {
            return (
                <div className={styles.semDados}>
                    Nenhum acesso registrado no período selecionado para este restaurante.
                </div>
            );
        }

        return (
            <>
                <div className={styles.cartoes}>
                    {resumo.totaisPorTipo.map(total => (
                        <div key={total.tipoAcesso} className={styles.cartao}>
                            <div className={styles.cartaoTitulo}>
                                <i className={IconeTipoAcesso[total.tipoAcesso]}
                                   style={{color: obterCor(total.tipoAcesso)}}></i>
                                {RotuloTipoAcesso[total.tipoAcesso]}
                            </div>
                            <div className={styles.cartaoTotal}>{total.total}</div>
                        </div>
                    ))}
                </div>

                <div className={styles.graficos}>
                    <div className={styles.graficoRosca}>
                        <div className={styles.tituloGrafico}>Distribuição por tipo de ação</div>
                        <Chart type="doughnut" data={dadosTotaisPorTipo} options={opcoesRosca}
                               style={{height: "320px"}}/>
                    </div>

                    <div className={styles.graficoLinha}>
                        <div className={styles.tituloGrafico}>Acessos por dia</div>
                        <Chart type="line" data={dadosTotaisPorDia} options={opcoesLinha}
                               style={{height: "320px"}}/>
                    </div>

                    {restauranteSelecionado === TODOS_RESTAURANTES && resumo.totaisPorRestaurante.length > 0 && (
                        <div className={styles.graficoCidade}>
                            <div className={styles.tituloGrafico}>Restaurantes mais acessados</div>
                            <Chart type="bar" data={dadosTotaisPorRestaurante} options={opcoesBarra}
                                   style={{height: `${Math.max(160, Math.min(resumo.totaisPorRestaurante.length, LIMITE_RESTAURANTES) * 45)}px`}}/>
                        </div>
                    )}

                    <div className={styles.graficoCidade}>
                        <div className={styles.tituloGrafico}>Acessos por cidade</div>
                        {resumo.totaisPorCidade.length === 0 ? (
                            <div className={styles.semCidade}>
                                Nenhum acesso com localização identificada no período.
                            </div>
                        ) : (
                            <Chart type="bar" data={dadosTotaisPorCidade} options={opcoesBarra}
                                   style={{height: `${Math.max(160, Math.min(resumo.totaisPorCidade.length, LIMITE_CIDADES) * 45)}px`}}/>
                        )}
                    </div>
                </div>
            </>
        );
    }

    return (
        <CustomView id={"analytics"} title={"Analytics do restaurante"}
                    mostrarCancelar={false} mostrarBarraBotoes={false}>
            <div className={styles.filtros}>
                <div className={styles.filtroCampo}>
                    <Combo id={"restaurante"} label={"Restaurante"}
                           value={restauranteSelecionado}
                           values={opcoesRestaurantes}
                           optionLabel={"nomeEstabelecimento"}
                           optionValue={"uuid"}
                           filter={true}
                           placeHolder={"Selecione o restaurante"}
                           onChangeHandler={(e: any) => setRestauranteSelecionado(e.value)}/>
                </div>

                <div className={styles.filtroCampo}>
                    <Calendario id={"dataInicial"} label={"Data inicial"}
                                value={dataInicial}
                                onChangeHandler={(e: any) => setDataInicial(e.value)}/>
                </div>

                <div className={styles.filtroCampo}>
                    <Calendario id={"dataFinal"} label={"Data final"}
                                value={dataFinal}
                                minDate={dataInicial}
                                onChangeHandler={(e: any) => setDataFinal(e.value)}/>
                </div>

                <div className={styles.filtroBotao}>
                    <Button label={"Filtrar"} icon={"pi pi-search"} raised size={"large"}
                            disabled={!restauranteSelecionado}
                            onClick={pesquisar}/>
                </div>
            </div>

            <div className={styles.espacamento}>
                {conteudo()}
            </div>
        </CustomView>
    );
}

export default RestauranteAnalyticsView;
